import {
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  IconButton,
  useMediaQuery
} from "@mui/material";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { showSuccessToast, showErrorToast } from "../../utils/toaster";
import { baseurl } from "../../config/url";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const UserProfile = ({ isSidebarOpen, setSidebarOpen, triggerLogin }) => {
  const [userDetails, setUserDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userId = localStorage.getItem("userId");

  const toggleSidebar = (open) => () => setSidebarOpen(open);

  const fetchUserDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${baseurl}/api/user/users-by-id/${userId}`);
      setUserDetails(response.data);
      setOriginalData(response.data);
    } catch (error) {
      showErrorToast("Failed to fetch user details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchUserDetails();
  }, [userId]);

  useEffect(() => {
    if (!userId) setUserDetails({});
    else fetchUserDetails();
  }, [userId]);

  const handleUpdate = async () => {
    if (
      userDetails.name === originalData.name &&
      userDetails.email === originalData.email &&
      userDetails.mobile === originalData.mobile
    ) {
      showErrorToast("No changes detected.");
      return false;
    }

    if (!userDetails.name?.trim()) {
      showErrorToast("Name cannot be empty.");
      return false;
    }

    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(userDetails.mobile.trim())) {
      showErrorToast("Please enter a valid 10-digit Indian mobile (starts with 6-9).");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userDetails.email)) {
      showErrorToast("Please enter a valid email address.");
      return false;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        showErrorToast("User not authenticated. Please log in again.");
        return;
      }

      const response = await axios.put(
        `${baseurl}/api/user/update-user/${userId}`,
        userDetails,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSuccessToast(response.data.message || "Profile updated successfully.");
      setOriginalData(userDetails);
      return true;
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update details");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Drawer
      anchor="right"
      open={isSidebarOpen}
      onClose={toggleSidebar(false)}
      PaperProps={{
        style: {
          width: isMobile ? "100%" : "600px",
          padding: "16px",
        },
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: "1.5rem", marginBottom: "16px" }}>User Profile</h2>
        <IconButton onClick={toggleSidebar(false)}>
          <CloseIcon />
        </IconButton>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : !userId ? (
        <div
          onClick={() => {
            setSidebarOpen(false);
            triggerLogin();
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            textAlign: 'center',
            color: '#555',
            padding: '24px',
            cursor: 'pointer',
          }}
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/1828/1828490.png"
            alt="Login Required"
            style={{ width: '100px', marginBottom: '16px', opacity: 0.7 }}
          />
          <h2 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>Please log in to view your profile</h2>
          <p style={{ fontSize: '0.95rem', color: '#777' }}>Click anywhere here to log in.</p>
        </div>
      ) : (
        <List>
          <Grid container spacing={2}>
            {[
              { label: "Name", key: "name" },
              { label: "Email", key: "email" },
              { label: "Mobile", key: "mobile" },
              { label: "Account Type", key: "type" },
            ].map((field, index) => (
              <Grid item xs={12} sm={6} key={index}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label={field.label}
                    name={field.key}
                    value={userDetails[field.key] || ""}
                    onChange={handleInputChange}
                    error={
                      field.key === "email" &&
                      !!userDetails.email &&
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)
                    }
                    helperText={
                      field.key === "email" &&
                      !!userDetails.email &&
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)
                        ? "Invalid email format"
                        : ""
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={<strong>{field.label}</strong>}
                      secondary={userDetails[field.key] || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>
            ))}

            {userDetails.type === "B2B" && (
              <Grid item xs={12} sm={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="GST Number"
                    name="gstNumber"
                    value={userDetails.gstNumber || ""}
                    onChange={handleInputChange}
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={<strong>GST Number</strong>}
                      secondary={userDetails.gstNumber || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <ListItem>
                <ListItemText
                  primary={<strong>Registration Date</strong>}
                  secondary={formatDate(userDetails.createdAt)}
                />
              </ListItem>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                fullWidth
                color={isEditing ? "success" : "primary"}
                onClick={async () => {
                  if (isEditing) {
                    const updated = await handleUpdate();
                    if (!updated) return;
                    setIsEditing(false);
                  } else {
                    setIsEditing(true);
                  }
                }}
              >
                {isEditing ? "Save Profile" : "Edit Profile"}
              </Button>
            </Grid>
          </Grid>
        </List>
      )}
    </Drawer>
  );
};

export default UserProfile;
